import { createStyles, Grid, makeStyles } from '@material-ui/core';
import { GridColumns, GridRowData } from '@mui/x-data-grid';
import { Form, Formik, FormikProps } from 'formik';
import fileDownload from 'js-file-download';
import React, { useContext, useMemo, useState } from 'react';
import { useAsync, useAsyncCallback } from 'react-async-hook';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import {
  ProductCatalogAvailableMaterialGroupsResponse,
  ProductCatalogResponse,
  useMurexinPimContextApi,
} from '../../../api';
import { NotificationContext } from '../../../contexts';
import { CountryAdminBreadCrumbs } from '../../../layouts/CountryAdminBreadCrumbs/CountryAdminBreadCrumbs';
import { IRouterParams } from '../../../routes';
import ListView from '../../../shared/Components/ListView';
import PIMButton from '../../../shared/Components/PIMButton';
import PIMIconButton from '../../../shared/Components/PIMIconButton';
import PIMTextField from '../../../shared/Components/PIMTextField';
import ReorderableListView from '../../../shared/Components/ReordableListView/ReordableListView';
import { CircleLoader } from '../../../utils/CircleLoader';

enum ReloadType {
  ProductCatalog,
  MaterialGroups,
  Both,
}

export function ProductCatalogConfiguration(): JSX.Element {
  const classes = useStyles();
  const api = useMurexinPimContextApi();
  const [searchText, setSearchText] = useState<string>('');
  const [searchTextMaterialGroups, setSearchTextMaterialGroups] = useState<string>('');
  const { t } = useTranslation();
  const { country, divisionId } = useParams<IRouterParams>();
  const notification = useContext(NotificationContext);

  const [productCatalog, setProductCatalog] = useState<ProductCatalogResponse[]>([]);
  const [materialGroupsForCatalog, setMaterialGroupsForCatalog] = useState<
    ProductCatalogAvailableMaterialGroupsResponse[]
  >([]);

  const [productCatalogReloadTrigger, setProductCatalogReloadTrigger] = useState(0);
  const [materialGroupReloadTrigger, setMaterialGroupReloadTrigger] = useState(0);

  const exportFilename = 'Produktkatalog Export.csv';

  const reloadData = (reloadType: ReloadType) => {
    switch (reloadType) {
      case ReloadType.ProductCatalog:
        setProductCatalogReloadTrigger((prev) => prev + 1);
        break;
      case ReloadType.MaterialGroups:
        setMaterialGroupReloadTrigger((prev) => prev + 1);
        break;
      case ReloadType.Both:
        setProductCatalogReloadTrigger((prev) => prev + 1);
        setMaterialGroupReloadTrigger((prev) => prev + 1);
    }
  };

  const productCatalogResponse = useAsync(async () => {
    const response = await api.productService.productServiceProductCatalogDetail(country, Number(divisionId));
    setProductCatalog(response.data);
    return response.data ?? [];
  }, [country, productCatalogReloadTrigger]);

  const productCatalogMaterialGroupResponse = useAsync(async () => {
    const response = await api.productService.productServiceMaterialGroupsForProductCatalogDetail(
      country,
      Number(divisionId)
    );
    setMaterialGroupsForCatalog(response.data);
    return response.data ?? [];
  }, [country, materialGroupReloadTrigger]);

  const columnDefinition: GridColumns = [
    { field: 'id', headerName: '', hide: true },
    {
      field: 'materialGroupName',
      headerName: t('Warengruppencode'),
      flex: 1,
      sortable: false,
    },
    {
      field: 'materialGroupDescription',
      headerName: t('Warengruppe'),
      flex: 3,
      sortable: false,
    },
    { field: 'variant', headerName: t('Variante'), flex: 2, sortable: false },
    {
      field: 'order',
      headerName: '',
      hide: true,
      sortable: false,
    },
  ];

  const materialGroupColumnDefinition: GridColumns = [
    { field: 'id', headerName: '', hide: true },
    {
      field: 'materialGroupName',
      headerName: t('Warengruppencode'),
      flex: 1,
    },
    {
      field: 'materialGroupDescription',
      headerName: t('Warengruppe'),
      flex: 3,
    },
    { field: 'variant', headerName: t('Variante'), flex: 1 },
  ];

  const filteredMaterialGroups = useMemo(() => {
    if (!searchTextMaterialGroups) return materialGroupsForCatalog;
    return materialGroupsForCatalog.filter((materialGroup) =>
      [
        materialGroup.materialGroupName,
        materialGroup.materialGroupDescription,
        materialGroup.ean,
        materialGroup.variant,
      ].some((field) => field?.toLowerCase().includes(searchTextMaterialGroups.toLowerCase()))
    );
  }, [materialGroupsForCatalog, searchTextMaterialGroups]);

  const handleProductCatalogRemoval = (
    row: GridRowData,
    formik: FormikProps<{ productCatalog: ProductCatalogResponse[] }>
  ) => {
    if (!row) return;

    setMaterialGroupsForCatalog((prev) => {
      const updatedMaterialGroups = [
        ...prev,
        {
          id: row.id,
          materialGroupName: row.materialGroupName,
          materialGroupDescription: row.materialGroupDescription,
          variant: row.variant,
        },
      ];

      return updatedMaterialGroups.sort((a, b) => {
        // Convert null/undefined to empty strings to avoid errors
        const materialGroupA = a.materialGroupName ?? '';
        const materialGroupB = b.materialGroupName ?? '';
        const descriptionA = a.materialGroupDescription ?? '';
        const descriptionB = b.materialGroupDescription ?? '';

        // Sort by materialGroupName first (lexicographically, numeric-aware)
        const materialGroupNameComparison = materialGroupA.localeCompare(materialGroupB, undefined);
        if (materialGroupNameComparison !== 0) return materialGroupNameComparison;

        // If materialGroupNames are equal, sort by materialGroupDescription (case-insensitive)
        return descriptionA.localeCompare(descriptionB, undefined, { sensitivity: 'base' });
      });
    });

    // Remove item from source data grid
    formik.setFieldValue(
      'productCatalog',
      formik.values.productCatalog.filter((item) => item.id !== row.id)
    );
  };

  const handleAddMaterialGroupToProductCatalog = (
    row: GridRowData,
    formik: FormikProps<{ productCatalog: ProductCatalogResponse[] }>
  ) => {
    if (!row) return;

    const maxOrderProduct =
      productCatalog.reduce((max, pc) => {
        return pc.order !== undefined && (max === null || pc.order > (max.order ?? -Infinity)) ? pc : max;
      }, null as ProductCatalogResponse | null)?.order ?? 0;

    // Move item to target data grid
    formik.setFieldValue('productCatalog', [
      ...formik.values.productCatalog,
      {
        id: row.id,
        materialGroupName: row.materialGroupName,
        materialGroupDescription: row.materialGroupDescription,
        variant: row.variant,
        order: maxOrderProduct + 1,
      },
    ]);

    // Remove item from source data grid
    setMaterialGroupsForCatalog((prev) => prev.filter((item) => item.id !== row.id));
  };

  const handleProductCatalogReorder = (
    formik: FormikProps<{ productCatalog: ProductCatalogResponse[] }>,
    sourceId: number,
    targetId: number
  ) => {
    const sourceIndex = formik.values.productCatalog.findIndex((r) => r.id === sourceId);
    const targetIndex = formik.values.productCatalog.findIndex((r) => r.id === targetId);
    if (sourceIndex < 0 || targetIndex < 0 || sourceIndex === targetIndex) return;

    const updatedRows = [...formik.values.productCatalog];
    const [movedRow] = updatedRows.splice(sourceIndex, 1);
    updatedRows.splice(targetIndex, 0, movedRow);
    formik.setFieldValue('productCatalog', updatedRows);
  };

  const exportCSV = useAsyncCallback(async () => {
    try {
      await exportProductCatalog();
      notification.success(t('Export erfolgreich'));
    } catch {
      notification.error(t('Export fehlgeschlagen'));
    }
  });

  const exportProductCatalog = async () => {
    const result = await api.productService.productServiceProductCatalogFileDetail(country, Number(divisionId));
    fileDownload(await result.blob(), exportFilename);
  };

  const handleOnSubmit = async (values: ProductCatalogResponse[]) => {
    try {
      if (values) {
        const productCatalogSaveRequest = {
          productCatalogSaveRequestItems: values.map((v) => ({
            materialGroupId: v.id,
            divisionId: Number(divisionId),
            countryCode: country,
          })),
        };
        await api.productService.productServiceProductCatalogCreate(
          country,
          Number(divisionId),
          productCatalogSaveRequest
        );
      }
      notification.success(t('Speichern erfolgreich'));
    } catch (error) {
      notification.error(t('Speichern fehlgeschlagen'));
    }
  };

  return (
    <>
      <CircleLoader open={productCatalogResponse.loading || productCatalogMaterialGroupResponse.loading} />
      <Grid container className={classes.Container}>
        <CountryAdminBreadCrumbs site={t('Produktkatalog')} />
        <Grid container item xs={12} className={classes.ProductCatalogPage}>
          <Formik
            initialValues={{ productCatalog: productCatalog }}
            onSubmit={async (values, { setSubmitting, resetForm }) => {
              await handleOnSubmit(values.productCatalog);
              reloadData(ReloadType.Both);
              resetForm();
            }}
            enableReinitialize={true}>
            {(formik) => {
              return (
                <>
                  <Grid container item xs={12}>
                    <Grid container item xs={8}>
                      <div className={classes.Title}>{t('Produktkatalog Konfigurieren und Exportieren')}</div>
                    </Grid>
                    <Grid container item xs={4} className={classes.ActionButtonRow}>
                      <PIMButton
                        className={classes.TopButtons}
                        icon='download'
                        color='primary'
                        busy={exportCSV.loading}
                        type='submit'
                        onClick={exportCSV.execute}
                        disabled={formik.dirty}>
                        {t('CSV exportieren')}
                      </PIMButton>
                      <PIMButton
                        className={classes.TopButtons}
                        icon='close'
                        type='reset'
                        disabled={!formik.dirty}
                        onClick={() => {
                          formik.resetForm();
                          reloadData(ReloadType.MaterialGroups);
                          notification.info(t('Daten zurückgesetzt'));
                        }}>
                        {t('Abbrechen')}
                      </PIMButton>
                      <PIMButton
                        color='primary'
                        icon='save'
                        className={classes.TopButtons}
                        busy={formik.isSubmitting}
                        disabled={!formik.dirty}
                        onClick={async () => {
                          await formik.submitForm();
                        }}>
                        {t('Speichern')}
                      </PIMButton>
                    </Grid>
                  </Grid>
                  <Grid item xs={6} className={classes.ProductCatalogGrid}>
                    <div className={classes.Title}>{t('Ausgewählte Warengruppen')}</div>
                    <Form>
                      <Grid item xs={12} className={classes.SearchPlaceholder}>
                        {/* <PIMTextField
                          label={t('Element suchen')}
                          className={classes.SearchTextField}
                          fullWidth
                          onChange={(event) => setSearchText(event.target.value ?? '')}
                        /> */}
                      </Grid>
                      <Grid item xs={12}>
                        <ReorderableListView
                          columns={[
                            ...columnDefinition,
                            {
                              field: '',
                              headerName: '',
                              sortable: false,
                              filterable: false,
                              hideSortIcons: true,
                              disableColumnMenu: true,
                              align: 'center',
                              width: 80,
                              // eslint-disable-next-line react/display-name
                              renderCell: (params) => (
                                <PIMIconButton
                                  className={classes.DeleteButton}
                                  size='small'
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleProductCatalogRemoval(params.row, formik);
                                  }}
                                  iconName='arrowCircleRight'
                                />
                              ),
                            },
                          ]}
                          data={formik.values.productCatalog}
                          onRowDoubleClick={(params) => handleProductCatalogRemoval(params.row, formik)}
                          onReorder={(sourceId: number, targetId: number) =>
                            handleProductCatalogReorder(formik, sourceId, targetId)
                          }
                          disabledSelection
                        />
                      </Grid>
                    </Form>
                  </Grid>
                  <Grid item xs={6} className={classes.ProductCatalogGrid}>
                    <div className={classes.Title}>{t('Warengruppen zum Produktkatalog hinzufügen')}</div>
                    <Grid item xs={12}>
                      <PIMTextField
                        label={t('Element suchen')}
                        className={classes.SearchTextField}
                        fullWidth
                        onChange={(event) => setSearchTextMaterialGroups(event.target.value ?? '')}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <ListView
                        columns={[
                          {
                            field: '',
                            headerName: '',
                            sortable: false,
                            filterable: false,
                            hideSortIcons: true,
                            disableColumnMenu: true,
                            align: 'center',
                            width: 80,
                            // eslint-disable-next-line react/display-name
                            renderCell: (params) => (
                              <PIMIconButton
                                className={classes.DeleteButton}
                                size='small'
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleAddMaterialGroupToProductCatalog(params.row, formik);
                                }}
                                iconName='arrowCircleLeft'
                              />
                            ),
                          },
                          ...materialGroupColumnDefinition,
                        ]}
                        data={filteredMaterialGroups}
                        disabledPagination
                        disabledSelection
                        onRowDoubleClick={(params) => {
                          if (params) {
                            handleAddMaterialGroupToProductCatalog(params.row, formik);
                          }
                        }}
                      />
                    </Grid>
                  </Grid>
                </>
              );
            }}
          </Formik>
        </Grid>
      </Grid>
    </>
  );
}

const useStyles = makeStyles(() =>
  createStyles({
    ProductCatalogPage: {
      padding: '24px 0px 0px 8px',
    },
    ProductCatalogGrid: {
      paddingRight: '24px',
      minHeight: '100%',
    },
    SearchTextField: {
      marginBottom: '24px',
    },
    SearchPlaceholder: {
      height: '56px',
      marginBottom: '24px',
    },
    TitleRow: {
      fontSize: '36px',
      padding: '0 0 24px 0',
    },
    Sidebar: {
      marginTop: '-100px',
      minHeight: '100%',
    },
    Container: {
      height: '100%',
      flexDirection: 'column',
      flexWrap: 'nowrap',
      margin: '0px 0px 0px 0px',
    },
    Title: {
      fontFamily: 'Roboto',
      fontStyle: 'normal',
      fontWeight: 300,
      fontSize: '36px',
      marginBottom: '24px',
      lineHeight: '45px',
    },
    ActionButtonRow: {
      flexWrap: 'nowrap',
      justifyContent: 'flex-end',
      paddingBottom: '32px',
    },
    TopButtons: {
      marginLeft: '24px',
    },
    DeleteButton: {
      marginRight: '2px',
    },
  })
);
